import { ValidatorFn, Validators } from '@angular/forms';

import { inject } from '@angular/core';
import { SuggestionsHintsService } from 'src/app/services/suggestions-hints.service';
import { CommunityStatus } from 'src/app/swagger-api';
import AppValues from '../../common/app.values';
import { Attribute, ElementStyles, SelectOptions, SelectorOption } from '../../interfaces/form-and-field.interface';
import FieldAllowedValueClasses from './field-allowed-value-classes.values';
import { FieldCustomValidators } from './field-custom.validators';

export default class FieldAttributesValues {
  protected readonly selectOptions: { [key: string]: () => SelectorOption[] } = {
    [FieldAllowedValueClasses.IS_OPEN_SELECT]: () => [
      { entity_id: CommunityStatus.Open, title: 'COMMUNITY_STATUS_STATE_PUBLIC' },
      { entity_id: CommunityStatus.Closed, title: 'COMMUNITY_STATUS_STATE_PRIVATE' },
    ],
    [FieldAllowedValueClasses.YES_NO_SELECT]: () => this.formSelectOption({ options: AppValues.yesNoSelectValues }),
    [FieldAllowedValueClasses.GENDER_SELECT]: () => this.formSelectOption({ options: AppValues.genderTypes }),
    [FieldAllowedValueClasses.SUFFIX_SELECT]: () =>
      this.formSelectOption({ options: AppValues.suffixes, needsEmpty: true, emptyName: 'NO_SUFFIX_HINT' }),
    [FieldAllowedValueClasses.LANGUAGES_SELECT]: () => this.formSelectOption({ options: AppValues.languages }),
    [FieldAllowedValueClasses.ORGANIZATIONS_TYPE_INPUT_SELECT]: () =>
      this.suggestionsHintsService.organizationTypeOptions,
    [FieldAllowedValueClasses.ROLES_SELECT]: () => this.suggestionsHintsService.individualRolesOptions,
    [FieldAllowedValueClasses.MAP_ROLES_SELECT]: () => this.suggestionsHintsService.individualRolesOptions,
  };

  protected readonly validatorsTypes: {
    [key: string]: (attribute: Attribute) => ValidatorFn[];
  } = {
    [FieldAllowedValueClasses.EMAIL]: (attribute: Attribute) => [
      FieldCustomValidators.patternValidator(AppValues.emailPattern, {
        email: true,
      }),
      ...(attribute.required ? this.requiredValidators(attribute) : []),
    ],
    [FieldAllowedValueClasses.VERIFIED_EMAIL]: (attribute: Attribute) => [
      FieldCustomValidators.patternValidator(AppValues.emailPattern, {
        verified_email: true,
      }),
      ...(attribute.required ? this.requiredValidators(attribute) : []),
    ],
    [FieldAllowedValueClasses.ALIAS]: (attribute: Attribute) => [
      FieldCustomValidators.patternValidator(AppValues.aliasPattern, {
        alias: true,
      }),
      ...(attribute.required ? this.requiredValidators(attribute) : []),
    ],
    [FieldAllowedValueClasses.ORGANIZATION_NAME]: (attribute: Attribute) => [
      FieldCustomValidators.patternValidator(AppValues.organizationNamePattern, {
        organizationName: true,
      }),
      ...(attribute.required ? this.requiredValidators(attribute) : []),
    ],
    [FieldAllowedValueClasses.ALPHABETIC]: (attribute: Attribute) => [
      FieldCustomValidators.patternValidator(AppValues.alphabeticPattern, {
        alphabetic: true,
      }),
      ...(attribute.required ? this.requiredValidators(attribute) : []),
    ],
    [FieldAllowedValueClasses.ALPHABETIC_NAME]: (attribute: Attribute) => [
      FieldCustomValidators.patternValidator(AppValues.alphabeticNamePattern, {
        alphabeticName: true,
      }),
      ...(attribute.required ? this.requiredValidators(attribute) : this.lengthValidators(attribute)),
    ],
    [FieldAllowedValueClasses.PHONE]: () => [],
    [FieldAllowedValueClasses.PHONE_2FA]: () => [],
    [FieldAllowedValueClasses.AUTHENTICATOR_BINDING]: () => [],
    [FieldAllowedValueClasses.ZIP_CODE]: (attribute: Attribute) => [
      FieldCustomValidators.patternValidator(AppValues.zipCodePattern, {
        zip_code: true,
      }),
      ...(attribute.required ? this.requiredValidators(attribute) : []),
    ],
    [FieldAllowedValueClasses.ALPHANUMERIC]: (attribute: Attribute) => [
      FieldCustomValidators.patternValidator(AppValues.alphanumericPattern, {
        alphanumeric: true,
      }),
      ...(attribute.required ? this.requiredValidators(attribute) : []),
    ],
    [FieldAllowedValueClasses.DATE]: (attribute: Attribute) => [
      FieldCustomValidators.patternValidator(AppValues.datePattern, { date: true }),
      FieldCustomValidators.notExistingDateValidator(),
      ...(attribute.required ? [Validators.required] : []),
      ...(attribute.name === 'start_date' || attribute.name === 'end_date'
        ? [FieldCustomValidators.dateRangeValidator(attribute)]
        : []),
      FieldCustomValidators.yearLessThenAllowed(),
    ],
    [FieldAllowedValueClasses.ORGANIZATIONS_TYPE_INPUT_SELECT]: (attribute: Attribute) =>
      this.baseValidators(attribute),
    [FieldAllowedValueClasses.URL]: (attribute: Attribute) => [
      FieldCustomValidators.patternValidator(AppValues.urlPattern, {
        url: true,
      }),
      ...(attribute.required ? this.requiredValidators(attribute) : this.lengthValidators(attribute)),
    ],
    [FieldAllowedValueClasses.PHOTO]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.FILE]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.MULTIPLE_FILES]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.TEXTAREA_FULL]: (attribute: Attribute) => [
      ...(attribute.required ? this.requiredValidators(attribute) : this.lengthValidators(attribute)),
    ],
    [FieldAllowedValueClasses.TEXTAREA]: (attribute: Attribute) => [
      ...(attribute.required ? this.requiredValidators(attribute) : this.lengthValidators(attribute)),
    ],
    [FieldAllowedValueClasses.CHECKBOX]: (attribute: Attribute) => this.requiredTrueValidator(attribute),
    [FieldAllowedValueClasses.TERMS_CHECKBOX]: (attribute: Attribute) => this.requiredTrueValidator(attribute),
    [FieldAllowedValueClasses.SWITCH]: (attribute: Attribute) => this.requiredTrueValidator(attribute),
    [FieldAllowedValueClasses.CHARACTERS]: (attribute: Attribute) => [
      ...(attribute.required ? this.requiredValidators(attribute) : []),
    ],
    [FieldAllowedValueClasses.IS_OPEN_SELECT]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.WARNING_LABLE]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.CHARITY_FOCUSES]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.DISEASES_SELECT]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.MAP_DISEASES_SELECT]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.YES_NO_SELECT]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.LANGUAGES_SELECT]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.GENDER_SELECT]: (attribute: Attribute) => [
      ...(attribute.required ? this.requiredValidators(attribute) : this.lengthValidators(attribute)),
    ],
    [FieldAllowedValueClasses.SUFFIX_SELECT]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.SPECIALTIES_SELECT]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.MAP_SPECIALTIES_SELECT]: (attribute: Attribute) => this.baseValidators(attribute),
    user_type_select: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.LINKED_ORGANIZATION_IDS_SELECT]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.ALTERNATIVE_NAME]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.LINKED_COMMUNITY_IDS_SELECT]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.LINKED_PROJECT_IDS_SELECT]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.PROJECTS_SEARCH_SELECT]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.COMMUNITIES_SEARCH_SELECT]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.CONNECT_COMMUNITY_SEARCH_SELECT]: (attribute: Attribute) =>
      this.baseValidators(attribute),
    verification_code: (attribute: Attribute) => [
      ...(attribute.required ? this.requiredValidators(attribute) : []),
      FieldCustomValidators.patternValidator(AppValues.numericPattern, {
        verification_code: true,
      }),
    ],
    [FieldAllowedValueClasses.LOCATION]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.CITY_LEVEL_LOCATION]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.LOCATION_ALL_TYPES]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.TEXT_LOCATION]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.FULL_ADDRESS_LOCATION]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.ROLES_SELECT]: (attribute: Attribute) => this.baseValidators(attribute),
    [FieldAllowedValueClasses.MAP_ROLES_SELECT]: (attribute: Attribute) => this.baseValidators(attribute),
  };

  protected readonly allowedTypes: string[] = Object.keys(this.validatorsTypes);
  protected readonly optionFieldTypes: string[] = Object.keys(this.selectOptions);

  protected readonly baseStyles: ElementStyles = {
    containerStyle: 'constructor-form-group',
    contentStyle: 'form-control',
  };

  protected readonly stylesForWarning: ElementStyles = {
    containerStyle: 'constructor-form-group pt-0',
    contentStyle: 'form-control',
  };

  protected readonly stylesForSelect: ElementStyles = {
    containerStyle: 'constructor-form-group mt-n1',
    contentStyle: 'form-control form-select',
  };

  protected readonly stylesForMultipleSearch: ElementStyles = {
    containerStyle: 'constructor-form-group',
    contentStyle: 'form-control control-container',
  };

  protected readonly stylesForSecondAuthLevel: ElementStyles = {
    containerStyle: 'constructor-form-group',
    contentStyle: 'form-control control-container',
    labelStyle: 'second-auth-level-label',
  };

  protected readonly stylesForFileField: ElementStyles = {
    containerStyle: 'constructor-form-group',
    contentStyle: 'custom-file-input',
  };

  protected readonly emptyStyles: ElementStyles = {
    containerStyle: '',
    contentStyle: '',
    labelStyle: '',
  };

  protected readonly styleClasses: { [key: string]: ElementStyles } = {
    [FieldAllowedValueClasses.EMAIL]: this.baseStyles,
    [FieldAllowedValueClasses.ALIAS]: this.baseStyles,
    [FieldAllowedValueClasses.ORGANIZATION_NAME]: this.baseStyles,
    [FieldAllowedValueClasses.ALPHABETIC_NAME]: this.baseStyles,
    [FieldAllowedValueClasses.ALPHABETIC]: this.baseStyles,
    [FieldAllowedValueClasses.TEXTAREA]: this.baseStyles,
    [FieldAllowedValueClasses.TEXTAREA_FULL]: {
      containerStyle: 'constructor-form-group',
      contentStyle: 'form-control textarea_full',
    },
    [FieldAllowedValueClasses.RICH_EDITOR]: {
      containerStyle: 'constructor-form-group',
      contentStyle: 'form-control rich_editor',
    },
    [FieldAllowedValueClasses.CHARACTERS]: this.baseStyles,
    [FieldAllowedValueClasses.ZIP_CODE]: this.baseStyles,
    [FieldAllowedValueClasses.ALPHANUMERIC]: this.baseStyles,
    [FieldAllowedValueClasses.PHONE]: this.baseStyles,
    [FieldAllowedValueClasses.PHONE_2FA]: this.stylesForSecondAuthLevel,
    [FieldAllowedValueClasses.AUTHENTICATOR_BINDING]: this.baseStyles,
    [FieldAllowedValueClasses.URL]: this.baseStyles,
    [FieldAllowedValueClasses.DATE]: this.baseStyles,
    [FieldAllowedValueClasses.CHECKBOX]: this.getSwitchCheckboxStyles('form-check'),
    [FieldAllowedValueClasses.TERMS_CHECKBOX]: this.getSwitchCheckboxStyles('form-check', 'pt-1 pb-0'),
    [FieldAllowedValueClasses.SWITCH]: this.getSwitchCheckboxStyles('custom-switch', 'constructor-form-group pt-3'),
    [FieldAllowedValueClasses.ORGANIZATIONS_TYPE_INPUT_SELECT]: this.stylesForMultipleSearch,
    [FieldAllowedValueClasses.PHOTO]: this.emptyStyles,
    [FieldAllowedValueClasses.FILE]: this.stylesForFileField,
    [FieldAllowedValueClasses.MULTIPLE_FILES]: this.stylesForFileField,
    [FieldAllowedValueClasses.YES_NO_SELECT]: this.stylesForSelect,
    [FieldAllowedValueClasses.LANGUAGES_SELECT]: this.stylesForMultipleSearch,
    [FieldAllowedValueClasses.DISEASES_SELECT]: this.stylesForMultipleSearch,
    [FieldAllowedValueClasses.MAP_DISEASES_SELECT]: this.stylesForMultipleSearch,
    [FieldAllowedValueClasses.LINKED_ORGANIZATION_IDS_SELECT]: this.stylesForMultipleSearch,
    [FieldAllowedValueClasses.LINKED_COMMUNITY_IDS_SELECT]: this.stylesForMultipleSearch,
    [FieldAllowedValueClasses.LINKED_PROJECT_IDS_SELECT]: this.stylesForMultipleSearch,
    [FieldAllowedValueClasses.GENDER_SELECT]: this.stylesForMultipleSearch,
    [FieldAllowedValueClasses.IS_OPEN_SELECT]: this.stylesForSelect,
    [FieldAllowedValueClasses.SUFFIX_SELECT]: this.stylesForSelect,
    [FieldAllowedValueClasses.SEARCH_SELECT]: this.stylesForSelect,
    [FieldAllowedValueClasses.ALTERNATIVE_NAME]: this.stylesForMultipleSearch,
    [FieldAllowedValueClasses.CHARITY_FOCUSES]: this.stylesForMultipleSearch,
    [FieldAllowedValueClasses.SPECIALTIES_SELECT]: this.stylesForMultipleSearch,
    [FieldAllowedValueClasses.MAP_SPECIALTIES_SELECT]: this.stylesForMultipleSearch,
    [FieldAllowedValueClasses.PROJECTS_SEARCH_SELECT]: this.stylesForMultipleSearch,
    [FieldAllowedValueClasses.COMMUNITIES_SEARCH_SELECT]: this.stylesForMultipleSearch,
    [FieldAllowedValueClasses.LOCATION]: this.baseStyles,
    [FieldAllowedValueClasses.ROLES_SELECT]: this.stylesForMultipleSearch,
    [FieldAllowedValueClasses.MAP_ROLES_SELECT]: this.stylesForMultipleSearch,
    [FieldAllowedValueClasses.CONNECT_COMMUNITY_SEARCH_SELECT]: this.stylesForMultipleSearch,
    [FieldAllowedValueClasses.LOCATION_ALL_TYPES]: this.baseStyles,
    [FieldAllowedValueClasses.FULL_ADDRESS_LOCATION]: this.baseStyles,
    [FieldAllowedValueClasses.CITY_LEVEL_LOCATION]: this.baseStyles,
    [FieldAllowedValueClasses.TEXT_LOCATION]: this.baseStyles,
    [FieldAllowedValueClasses.WARNING_LABLE]: this.stylesForWarning,
  };

  private suggestionsHintsService = inject(SuggestionsHintsService);

  protected readonly requiredTrueValidator: (attribute: Attribute) => ValidatorFn[] = (attribute: Attribute) => [
    ...(attribute.required ? [Validators.requiredTrue] : []),
  ];

  private baseValidators: (attribute: Attribute) => ValidatorFn[] = (attribute: Attribute) => [
    ...(attribute.required ? [Validators.required] : []),
  ];
  private requiredValidators: (attribute: Attribute) => ValidatorFn[] = (attribute: Attribute) => [
    Validators.required,
    Validators.minLength(attribute.min_length),
    Validators.maxLength(attribute.max_length),
  ];

  private lengthValidators: (attribute: Attribute) => ValidatorFn[] = (attribute: Attribute) => [
    Validators.minLength(attribute.min_length),
    Validators.maxLength(attribute.max_length),
  ];

  private getSwitchCheckboxStyles(containerStyle: string, additionalStyle = 'constructor-form-group'): ElementStyles {
    return {
      containerStyle: `${additionalStyle} ${containerStyle}`,
      contentStyle: 'form-check-input',
      labelStyle: 'form-label',
    };
  }

  private formSelectOption(selectOptions: SelectOptions): SelectorOption[] {
    return selectOptions.options.map((option: string) => ({
      entity_id: option,
      title: selectOptions.needsEmpty && option === '' ? selectOptions.emptyName : option,
    }));
  }
}
